import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Chip,
  Autocomplete,
  Alert,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { UserPreferences } from '../../types/userTypes';

const UserPreferencesForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, updatePreferences } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>({
    language: 'en',
    theme: 'auto',
    favoriteCurrencies: [],
    defaultFromCurrency: 'USD',
    defaultToCurrency: 'EUR',
    calculatorSettings: {
      decimalPlaces: 2,
      autoRefresh: true
    },
    watchlist: [],
    conversionHistory: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Common currencies for autocomplete
  const commonCurrencies = [
    'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'SEK', 'NZD',
    'MXN', 'SGD', 'HKD', 'NOK', 'TRY', 'RUB', 'INR', 'BRL', 'ZAR', 'KRW',
    'IRR', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'LBP', 'EGP'
  ];

  useEffect(() => {
    if (user) {
      setPreferences(user.preferences);
    }
  }, [user]);

  const handleLanguageChange = (language: 'en' | 'fr' | 'fa' | 'zh' | 'es') => {
    setPreferences(prev => ({ ...prev, language }));
    i18n.changeLanguage(language);
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'auto') => {
    setPreferences(prev => ({ ...prev, theme }));
    // Update theme in localStorage
    localStorage.setItem('theme', theme);
    // Trigger theme change in parent component
    window.dispatchEvent(new CustomEvent('themeChange', { detail: theme }));
  };

  const handleCurrencyAdd = (currency: string) => {
    if (currency && !preferences.favoriteCurrencies.includes(currency)) {
      setPreferences(prev => ({
        ...prev,
        favoriteCurrencies: [...prev.favoriteCurrencies, currency]
      }));
    }
  };

  const handleCurrencyRemove = (currency: string) => {
    setPreferences(prev => ({
      ...prev,
      favoriteCurrencies: prev.favoriteCurrencies.filter(c => c !== currency)
    }));
  };

  const handleSave = async () => {
    console.log('🔧 UserPreferencesForm: Save button clicked');
    if (!user) {
      console.log('🔧 UserPreferencesForm: No user found, cannot save');
      return;
    }

    console.log('🔧 UserPreferencesForm: Current preferences:', preferences);
    setIsLoading(true);
    setMessage(null);

    try {
      console.log('🔧 UserPreferencesForm: Calling updatePreferences...');
      await updatePreferences(preferences);
      console.log('🔧 UserPreferencesForm: Preferences updated successfully');
      setMessage({ type: 'success', text: t('user.preferencesUpdated') });
    } catch (error) {
      console.error('🔧 UserPreferencesForm: Error updating preferences:', error);
      setMessage({ type: 'error', text: t('user.preferencesUpdateError') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (user) {
      setPreferences(user.preferences);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Language and Theme */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('user.languageAndTheme')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('user.language')}</InputLabel>
              <Select
                value={preferences.language}
                label={t('user.language')}
                onChange={(e) => handleLanguageChange(e.target.value)}
              >
                <MenuItem value="en">{t('language.english')}</MenuItem>
                <MenuItem value="fr">{t('language.french')}</MenuItem>
                <MenuItem value="fa">{t('language.farsi')}</MenuItem>
                <MenuItem value="zh">{t('language.chinese')}</MenuItem>
                <MenuItem value="es">{t('language.spanish')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('user.theme')}</InputLabel>
              <Select
                value={preferences.theme}
                label={t('user.theme')}
                onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark' | 'auto')}
              >
                <MenuItem value="light">{t('theme.light')}</MenuItem>
                <MenuItem value="dark">{t('theme.dark')}</MenuItem>
                <MenuItem value="auto">{t('theme.auto')}</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Box>

        {/* Default Currencies */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('user.defaultCurrencies')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('user.defaultFromCurrency')}</InputLabel>
              <Select
                value={preferences.defaultFromCurrency}
                label={t('user.defaultFromCurrency')}
                onChange={(e) => setPreferences(prev => ({ ...prev, defaultFromCurrency: e.target.value }))}
              >
                {commonCurrencies.map(currency => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('user.defaultToCurrency')}</InputLabel>
              <Select
                value={preferences.defaultToCurrency}
                label={t('user.defaultToCurrency')}
                onChange={(e) => setPreferences(prev => ({ ...prev, defaultToCurrency: e.target.value }))}
              >
                {commonCurrencies.map(currency => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        </Box>

        {/* Favorite Currencies */}
        <Box sx={{ width: '100%' }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('user.favoriteCurrencies')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Autocomplete
              options={commonCurrencies}
              value={null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleCurrencyAdd(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('user.addFavoriteCurrency')}
                  placeholder={t('user.selectCurrency')}
                />
              )}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {preferences.favoriteCurrencies.map((currency) => (
                <Chip
                  key={currency}
                  label={currency}
                  onDelete={() => handleCurrencyRemove(currency)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Calculator Settings */}
        <Box sx={{ width: '100%' }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('user.calculatorSettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('user.decimalPlaces')}</InputLabel>
              <Select
                value={preferences.calculatorSettings.decimalPlaces}
                label={t('user.decimalPlaces')}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  calculatorSettings: {
                    ...prev.calculatorSettings,
                    decimalPlaces: e.target.value as number
                  }
                }))}
              >
                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.calculatorSettings.autoRefresh}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    calculatorSettings: {
                      ...prev.calculatorSettings,
                      autoRefresh: e.target.checked
                    }
                  }))}
                />
              }
              label={t('user.autoRefreshRates')}
            />

          </Paper>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={isLoading}
        >
          {t('common.reset')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? t('common.saving') : t('common.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default UserPreferencesForm;
